import React from 'react';
import { useParams } from 'react-router';
import { useNavigateSafe } from 'apps/sigma2-ui/src/lib/hooks/useNavigateSafe';
import { getApiBaseUrl } from 'apps/sigma2-ui/src/constants/environment';
import { useCallback } from 'react';
import { useEffect } from 'react';
import { useState } from 'react';
import { useSimpleReadPattern } from 'apps/sigma2-ui/src/lib/hooks/useSimpleReadPattern';
import { DatabaseRevisionForPatch } from 'libs/end-to-end/src/types/databases/revisions/database-revision';
import { DatabaseRevisions } from 'libs/end-to-end/src/types/databases/revisions/database-revision';
import { Database } from 'libs/end-to-end/src/types/databases/database';
import { useAsyncCallback } from 'apps/sigma2-ui/src/lib/hooks/useAsyncCallback';
import { getCustomFilterSets } from 'libs/end-to-end/src/filters-v2/filter-utils';
import { getDefaultFilterSetFilters } from 'libs/end-to-end/src/filters-v2/filter-utils';
import { EmptyObject } from 'libs/end-to-end/src/generic';
import { useUser } from 'apps/sigma2-ui/src/state/user';

export const useDatabaseRevisionsPage = () => {
  const { organizationId, databaseId } = useParams();
  const navigate = useNavigateSafe();
  const { userAuthenticatedApiCall } = useUser();

  const getDatabaseRevisionsUrlFn = useCallback(() => {
    return `${getApiBaseUrl()}/organizations/${organizationId}/databases/${databaseId}/revisions`;
  }, [organizationId, databaseId]);

  const getDatabaseRevisionsReadPattern = useSimpleReadPattern({
    getUrlFn: getDatabaseRevisionsUrlFn,
    canRead: true,
    responseSchema: DatabaseRevisions,
  });

  const getDatabaseByIdUrlFn = useCallback(() => {
    return `${getApiBaseUrl()}/organizations/${organizationId}/databases/${databaseId}`;
  }, [organizationId, databaseId]);

  const getDatabaseByIdReadPattern = useSimpleReadPattern({
    getUrlFn: getDatabaseByIdUrlFn,
    canRead: true,
    responseSchema: Database,
  });

  const [selectedDatabase, setSelectedDatabase] = useState<Database | null>(
    null,
  );

  const [currentDatabaseRevisions, setCurrentDatabaseRevisions] =
    useState<DatabaseRevisions | null>(null);

  useEffect(() => {
    if (getDatabaseRevisionsReadPattern.status === 'done') {
      setCurrentDatabaseRevisions(getDatabaseRevisionsReadPattern.entity);
    }
  }, [getDatabaseRevisionsReadPattern]);

  useEffect(() => {
    if (getDatabaseByIdReadPattern.status === 'done') {
      setSelectedDatabase(getDatabaseByIdReadPattern.entity);
    }
  }, [getDatabaseByIdReadPattern]);

  if (!organizationId || !databaseId) {
    throw new Error('organizationId and databaseId are required');
  }

  const onSave = useAsyncCallback(
    async (
      isAlive,
      {
        revisionId,
        isHidden,
      }: {
        revisionId: string;
        isHidden: boolean;
      },
    ) => {
      if (!isAlive()) {
        return;
      }

      const schemaToSave: DatabaseRevisionForPatch = {
        isHidden,
      };

      const response = await userAuthenticatedApiCall({
        url: `${getApiBaseUrl()}/organizations/${organizationId}/databases/${databaseId}/revisions/${revisionId}`,
        requestOptions: {
          method: 'PATCH',
          body: JSON.stringify(schemaToSave),
        },
        responseSchema: EmptyObject,
      });

      if (!response) {
        // failed to save
        return;
      }

      if (response.resultType === 'err') {
        // failed to save
        return;
      }

      if (getDatabaseRevisionsReadPattern.status !== 'loading') {
        getDatabaseRevisionsReadPattern.reloadEntity();
      }
    },
    [
      userAuthenticatedApiCall,
      organizationId,
      databaseId,
      getDatabaseRevisionsReadPattern,
    ],
  );

  return {
    organizationId,
    databaseId,
    revisions: currentDatabaseRevisions,
    database: selectedDatabase,
    navigate,
    onSave,
  };
};
